import { useState, type ChangeEvent, type FormEvent } from "react";
import Reveal from "./Reveal";
import { SITE, whatsappLink } from "@/constants/site";

interface FormState {
  fullName: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  adults: string;
  children: string;
  note: string;
}

const INITIAL: FormState = {
  fullName: "",
  phone: "",
  checkIn: "",
  checkOut: "",
  adults: "2",
  children: "0",
  note: "",
};

export default function ReservationForm() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState("");

  const update =
    (key: keyof FormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.phone.trim()) {
      setError("Lütfen ad soyad ve telefon bilgilerinizi girin.");
      return;
    }
    setError("");

    const lines = [
      "Merhaba Düzbağ Life, rezervasyon için bilgi almak istiyorum.",
      "",
      `Ad Soyad: ${form.fullName.trim()}`,
      `Telefon: ${form.phone.trim()}`,
      `Giriş Tarihi: ${form.checkIn || "-"}`,
      `Çıkış Tarihi: ${form.checkOut || "-"}`,
      `Yetişkin: ${form.adults}`,
      `Çocuk: ${form.children}`,
    ];
    if (form.note.trim()) lines.push(`Not: ${form.note.trim()}`);

    window.open(whatsappLink(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  const inputClass =
    "w-full bg-background-950/40 border border-background-700 text-background-50 placeholder-background-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 transition-colors";
  const labelClass = "block text-xs uppercase tracking-[0.15em] text-background-300 mb-2";

  return (
    <section id="rezervasyon" className="w-full bg-primary-800">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left copy */}
          <Reveal className="lg:col-span-5">
            <span className="text-accent-400 text-xs tracking-[0.3em] uppercase">
              Rezervasyon
            </span>
            <h2 className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl text-background-50 leading-[1.15] font-medium">
              DÜZBAĞ LIFE'TA
              <br />
              YERİNİZİ AYIRTIN
            </h2>
            <div className="mt-6 h-px w-16 bg-accent-500" />
            <p className="mt-6 text-background-200 text-base md:text-lg leading-relaxed">
              Tarihlerinizi gönderin, uygunluk ve rezervasyon detayları için
              sizinle WhatsApp üzerinden iletişime geçelim.
            </p>

            <div className="mt-8 flex flex-col gap-4">
              <a
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex items-center gap-3 text-background-50 cursor-pointer hover:text-accent-300 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10">
                  <i className="ri-phone-line text-lg" />
                </span>
                {SITE.phoneDisplay}
              </a>
              <a
                href={SITE.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-background-50 cursor-pointer hover:text-accent-300 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10">
                  <i className="ri-instagram-line text-lg" />
                </span>
                {SITE.instagramHandle}
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-background-950/30 border border-background-700/70 rounded-lg p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="fullName">
                    Ad Soyad
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={form.fullName}
                    onChange={update("fullName")}
                    className={inputClass}
                    placeholder="Adınız ve soyadınız"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="phone">
                    Telefon
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className={inputClass}
                    placeholder="05xx xxx xx xx"
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="checkIn">
                    Giriş Tarihi
                  </label>
                  <input
                    id="checkIn"
                    name="checkIn"
                    type="date"
                    value={form.checkIn}
                    onChange={update("checkIn")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="checkOut">
                    Çıkış Tarihi
                  </label>
                  <input
                    id="checkOut"
                    name="checkOut"
                    type="date"
                    value={form.checkOut}
                    onChange={update("checkOut")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="adults">
                    Yetişkin Sayısı
                  </label>
                  <select
                    id="adults"
                    name="adults"
                    value={form.adults}
                    onChange={update("adults")}
                    className={inputClass}
                  >
                    {["1", "2", "3", "4", "5", "6+"].map((n) => (
                      <option key={n} value={n} className="text-foreground-950">
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="children">
                    Çocuk Sayısı
                  </label>
                  <select
                    id="children"
                    name="children"
                    value={form.children}
                    onChange={update("children")}
                    className={inputClass}
                  >
                    {["0", "1", "2", "3", "4+"].map((n) => (
                      <option key={n} value={n} className="text-foreground-950">
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="note">
                    Not / Özel Talep
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    value={form.note}
                    onChange={update("note")}
                    maxLength={500}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder="Eklemek istediğiniz bir not (opsiyonel)"
                  />
                </div>
              </div>

              {error && (
                <p className="mt-5 text-sm text-accent-300 flex items-center gap-2">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold py-4 rounded-md whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
                UYGUNLUK SOR
              </button>
              <p className="mt-3 text-center text-xs text-background-400">
                Bilgileriniz WhatsApp mesajına dönüştürülür ve sohbet açılır.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}